import MalKort from '../components/MalKort';
import React, { useState, useEffect } from 'react';
import { Clock, SubNav, Metric, KpiRow, NorderiKpi, PlaneringTab } from '../components/SharedComponents';
import { tkr, pct, fmt, fmtMonth, fmtDate, fyMonthKeys, fyWeekKeys, raLabel } from '../utils';
import { getWeeklyData, getRoomStats, getBelaggning } from '../qlik';

// ── HELPERS ───────────────────────────────────────────────────────
function belaggningColor(b) {
  const v = b * 100;
  if (v >= 60) return '#1D9E75';
  if (v >= 30) return '#E0A458';
  return '#D85A30';
}
function veckodag(dateStr) {
  const d = new Date(dateStr);
  return ['Sön','Mån','Tis','Ons','Tor','Fre','Lör'][d.getDay()];
}
function kortDatum(dateStr) {
  const d = new Date(dateStr);
  return d.getDate() + '/' + (d.getMonth() + 1);
}

// ── NY BOKNING-RAD (vem, rum, ankomst, nätter) ───────────────────
function BokningRad({ b, muted }) {
  const detalj = [
    b.enhet || null,
    b.fran ? `ank. ${fmtDate(b.fran)}` : null,
    b.natter > 0 ? `${b.natter} ${b.natter === 1 ? 'natt' : 'nätter'}` : null,
    b.gaster > 0 ? `${b.gaster} gäster` : null,
  ].filter(Boolean).join(' · ');
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '8px 0', borderBottom: '1px solid #f3f3ef', fontFamily: 'system-ui,sans-serif' }}>
      <div style={{ minWidth: 0, marginRight: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: muted ? '#888' : 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {b.namn || `Bokning #${b.boknr}`}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
          {`#${b.boknr}`}{detalj ? ` · ${detalj}` : ''}
        </div>
      </div>
      <span style={{ fontSize: 13, fontWeight: 600, color: muted ? '#888' : 'var(--text)', flexShrink: 0 }}>{Math.round(b.varde / 1000)} tkr</span>
    </div>
  );
}

// ── DAGLIG HOTELLÖVERSIKT ─────────────────────────────────────────
function HotelDailyOverview({ bookings, monthly, newBookings }) {
  // Visa loading endast om bookings saknas helt (null/undefined)
  // Ett tomt objekt med belaggning7:[] är giltigt svar (ingen data idag)
  if (!bookings || bookings.belaggningDays === undefined) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 32, color: '#888' }}>
        Hämtar bokningsdata...
      </div>
    );
  }

  const {
    belaggningDays = [],
    checkins = [],
    checkouts = [],
    totalCheckins = 0,
    totalCheckouts = 0,
  } = bookings;

  // idag = index 7 i 28-dagarsarrayen (7 bakåt + idag + 20 framåt)
  const idag = belaggningDays[7] || {};
  const idagBelaggning = idag.belaggning || 0;
  const idagBokadeRum  = idag.bokadeRum  || 0;
  const idagMojliga    = idag.mojligaRum || 45;

  // Gäster som sover ikväll = ungefärlig frukost imorgon
  const frukostImorgon = idag.bokadeGaster || 0;

  return (
    <>
      {/* ── KPI-rad ── */}
      <div className="grid-4">
        <div className="metric">
          <div className="metric-label">Incheckningar idag</div>
          <div className="metric-value">{totalCheckins}</div>
          <div className="metric-sub">{checkins.length} enhet{checkins.length !== 1 ? 'er' : ''}</div>
        </div>
        <div className="metric">
          <div className="metric-label">Beläggning idag</div>
          <div className="metric-value" style={{ color: belaggningColor(idagBelaggning) }}>
            {Math.round(idagBelaggning * 100)}%
          </div>
          <div className="metric-sub">{idagBokadeRum} av {idagMojliga} rum</div>
        </div>
        <div className="metric">
          <div className="metric-label">Utcheckningar idag</div>
          <div className="metric-value">{totalCheckouts}</div>
          <div className="metric-sub">{checkouts.length} enhet{checkouts.length !== 1 ? 'er' : ''}</div>
        </div>
        <div className="metric">
          <div className="metric-label">Frukost imorgon</div>
          <div className="metric-value">~{frukostImorgon}</div>
          <div className="metric-sub">bokade gäster</div>
        </div>
      </div>

      {/* ── 7-dagars bokningsläge ── */}
      <div className="card">
        <div className="card-title">Bokningsläge — kommande 7 dagar</div>
        <div className="week-grid">
          {belaggningDays.slice(7, 14).map((dag, i) => {
            const b = dag.belaggning;
            const color = belaggningColor(b);
            const isToday = i === 0;
            return (
              <div
                key={i}
                className={'week-day' + (isToday ? ' week-day-today' : '')}
                style={{ borderTop: `3px solid ${color}` }}
              >
                <div className="week-day-name">{veckodag(dag.datum)}</div>
                <div className="week-day-date">{kortDatum(dag.datum)}</div>
                <div className="week-day-pct" style={{ color }}>{Math.round(b * 100)}%</div>
                <div className="week-day-rum">{dag.bokadeRum} rum · {dag.bokadeGaster || 0} pers</div>
              </div>
            );
          })}
        </div>
        <div className="week-legend">
          <span style={{ color: '#1D9E75' }}>■</span> &gt;60% Bra &nbsp;
          <span style={{ color: '#E0A458' }}>■</span> 30–60% OK &nbsp;
          <span style={{ color: '#D85A30' }}>■</span> &lt;30% Svag
        </div>
        <div className="note">Källa: Qlik Bokningar — Produkttyp accommodation, Status order/invoiced/payed</div>
      </div>

      {/* ── Nya bokningar ── */}
      {newBookings && (
        <div className="grid-2">
          <div className="card">
            <div className="card-title">Nya bokningar idag</div>
            {newBookings.idag.antal === 0 ? (
              <div style={{ color: '#aaa', fontSize: 13, fontFamily: 'system-ui', padding: '8px 0' }}>Inga nya bokningar idag</div>
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                  <span style={{ fontSize: 28, fontWeight: 700, color: '#1B2A4A' }}>{newBookings.idag.antal}</span>
                  <span style={{ fontSize: 18, fontWeight: 600, color: '#1D9E75', alignSelf: 'center' }}>{Math.round(newBookings.idag.varde / 1000)} tkr</span>
                </div>
                {newBookings.idag.bokningar.slice(0, 8).map((b, i) => (
                  <BokningRad key={i} b={b} />
                ))}
              </>
            )}
            <div className="note">Källa: Rådata Visbook — registration_date</div>
          </div>
          <div className="card">
            <div className="card-title">Nya bokningar igår</div>
            {newBookings.igar.antal === 0 ? (
              <div style={{ color: '#aaa', fontSize: 13, fontFamily: 'system-ui', padding: '8px 0' }}>Inga nya bokningar igår</div>
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                  <span style={{ fontSize: 28, fontWeight: 700, color: '#888' }}>{newBookings.igar.antal}</span>
                  <span style={{ fontSize: 18, fontWeight: 600, color: '#888', alignSelf: 'center' }}>{Math.round(newBookings.igar.varde / 1000)} tkr</span>
                </div>
                {newBookings.igar.bokningar.slice(0, 8).map((b, i) => (
                  <BokningRad key={i} b={b} muted />
                ))}
              </>
            )}
            <div className="note">Källa: Rådata Visbook — registration_date</div>
          </div>
        </div>
      )}

      {/* ── Check-ins och check-outs ── */}
      <div className="grid-2">
        <div className="card">
          <div className="card-title">Incheckningar idag — {totalCheckins} bokningar</div>
          {checkins.length === 0 ? (
            <div className="note" style={{ padding: '12px 0' }}>Inga incheckningar idag</div>
          ) : (
            [
              { typ: 'rum',      rubrik: 'Hotellrum' },
              { typ: 'stuga',    rubrik: 'Stugor' },
              { typ: 'lagenhet', rubrik: 'Lägenheter' },
              { typ: 'ovrigt',   rubrik: 'Övrigt' },
            ].map(({ typ, rubrik }) => {
              const grupp = checkins.filter(c => c.typ === typ);
              if (grupp.length === 0) return null;
              return (
                <div key={typ} style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>{rubrik}</div>
                  {grupp.map((c, i) => (
                    <KpiRow key={i} label={c.enhet} value={c.gaster + ' gäst' + (c.gaster !== 1 ? 'er' : '')} sub={c.natter + ' nätter'} />
                  ))}
                </div>
              );
            })
          )}
        </div>
        <div className="card">
          <div className="card-title">Utcheckningar idag — {totalCheckouts} bokningar</div>
          {checkouts.length === 0 ? (
            <div className="note" style={{ padding: '12px 0' }}>Inga utcheckningar idag</div>
          ) : (
            [
              { typ: 'rum',      rubrik: 'Hotellrum' },
              { typ: 'stuga',    rubrik: 'Stugor' },
              { typ: 'lagenhet', rubrik: 'Lägenheter' },
              { typ: 'ovrigt',   rubrik: 'Övrigt' },
            ].map(({ typ, rubrik }) => {
              const grupp = checkouts.filter(c => c.typ === typ);
              if (grupp.length === 0) return null;
              return (
                <div key={typ} style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>{rubrik}</div>
                  {grupp.map((c, i) => (
                    <KpiRow key={i} label={c.enhet} value={c.gaster + ' gäst' + (c.gaster !== 1 ? 'er' : '')} />
                  ))}
                </div>
              );
            })
          )}
        </div>
      </div>
    </>
  );
}

// ── INTÄKT LINJEDIAGRAM ──────────────────────────────────────────
// RÅ-nycklar och etikett beräknas dynamiskt (jul–jun) — hårdkodade
// listor gav rollover-buggen där allt satt fast på RÅ 25/26 efter 1 juli.
const FY_ORDER = fyMonthKeys();
const RA_LBL = raLabel();
const MONTH_LABELS = ["jul","aug","sep","okt","nov","dec","jan","feb","mar","apr","maj","jun"];

// Personal% = lönekostnad / intäkt (lön lagras negativt → abs). Färg: låg=grön, hög=röd.
const persPct = (lon, rev) => rev > 0 ? Math.round(Math.abs(lon || 0) / rev * 100) : null;
const persColor = (p) => p == null ? '#ccc' : p <= 35 ? '#1D9E75' : p >= 55 ? '#D85A30' : '#B8860B';

function LineChart({ series, labels, yFmt, tipFmt, tipLabels, todayIdx }) {
  const [hoverIdx, setHoverIdx] = React.useState(null);
  const W = 680, H = 200, pL = 50, pR = 16, pT = 24, pB = 28;
  const cW = W - pL - pR, cH = H - pT - pB;
  const n = labels.length;

  const allVals = series.flatMap(s => s.data);
  const maxVal = Math.max(...allVals, 1);
  const xOf = i => pL + (i / (n - 1)) * cW;
  const yOf = v => pT + cH - (v / maxVal) * cH;
  const linePath = data =>
    data.map((v, i) => `${i === 0 ? 'M' : 'L'}${xOf(i).toFixed(1)},${yOf(v).toFixed(1)}`).join(' ');

  const yTicks = [0, 0.25, 0.5, 0.75, 1];

  function handleMouseMove(e) {
    const svg = e.currentTarget;
    const rect = svg.getBoundingClientRect();
    const mx = (e.clientX - rect.left) * (W / rect.width);
    const step = cW / (n - 1);
    const idx = Math.round((mx - pL) / step);
    setHoverIdx(idx >= 0 && idx < n ? idx : null);
  }

  const hi = hoverIdx;
  const tipX = hi !== null ? xOf(hi) : 0;
  const tipOnRight = hi !== null && tipX < W / 2;
  const TIP_W = 130, TIP_H = 16 + series.length * 18;
  const tipBx = tipOnRight ? tipX + 10 : tipX - TIP_W - 10;

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      style={{ width: '100%', height: 'auto', display: 'block', cursor: 'crosshair' }}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => setHoverIdx(null)}
    >
      {/* Skuggning och "Idag"-linje */}
      {todayIdx != null && (
        <>
          <rect x={pL} y={pT} width={xOf(todayIdx) - pL} height={cH} fill="var(--weekend-band, #F0EFE9)" opacity={0.55} />
          <line x1={xOf(todayIdx)} y1={pT} x2={xOf(todayIdx)} y2={pT + cH}
            stroke="#1B2A4A" strokeWidth={1.5} strokeDasharray="4 3" opacity={0.35} />
          <text x={xOf(todayIdx) + 4} y={pT + 11} fontSize={9} fill="#1B2A4A" opacity={0.5} fontWeight="600">Idag</text>
        </>
      )}

      {yTicks.map(f => {
        const y = yOf(f * maxVal);
        const v = f * maxVal;
        return (
          <g key={f}>
            <line x1={pL} y1={y} x2={W-pR} y2={y} stroke="#E8E6E0" strokeWidth={1} />
            {f > 0 && <text x={pL-5} y={y+4} textAnchor="end" fontSize={10} fill="#AAA">{yFmt ? yFmt(v) : Math.round(v)}</text>}
          </g>
        );
      })}

      {series.map((s, si) => {
        const col = s.dash ? '#C2A878' : '#1d6a5f'; // heldragen=teal (i år), streckad=guld (fg år)
        return (
        <g key={si}>
          <path d={`${linePath(s.data)} L${xOf(n - 1).toFixed(1)},${yOf(0).toFixed(1)} L${xOf(0).toFixed(1)},${yOf(0).toFixed(1)} Z`} fill={col} opacity={s.dash ? 0.10 : 0.16} stroke="none" />
          <path d={linePath(s.data)} fill="none" stroke={col} strokeWidth={s.dash ? 1.3 : 1.8} strokeDasharray={s.dash || undefined} />
          {s.data.map((v, i) => v > 0 && (
            <circle key={i} cx={xOf(i)} cy={yOf(v)} r={hi === i ? 3.5 : 2} fill={col}
              stroke={hi === i ? '#fff' : 'none'} strokeWidth={1.2} />
          ))}
        </g>
        );
      })}

      {/* hover-linje */}
      {hi !== null && (
        <line x1={xOf(hi)} y1={pT} x2={xOf(hi)} y2={pT + cH} stroke="#CCC" strokeWidth={1} strokeDasharray="3 2" />
      )}

      {labels.map((lbl, i) => {
        const lines = String(lbl).split('\n'); // stöd för flerradig etikett (veckodag + datum)
        return (
          <text key={i} x={xOf(i)} y={H - pB + 12} textAnchor="middle" fontSize={lines.length > 1 ? 8.5 : 10}
            fill={hi === i ? '#333' : '#999'} fontWeight={hi === i ? '600' : 'normal'}>
            {lines.map((ln, li) => <tspan key={li} x={xOf(i)} dy={li === 0 ? 0 : 9.5}>{ln}</tspan>)}
          </text>
        );
      })}

      {/* legend */}
      {series.map((s, si) => (
        <g key={si} transform={`translate(${pL + si * 100}, 0)`}>
          <line x1={2} y1={pT-10} x2={22} y2={pT-10} stroke={s.dash ? '#C2A878' : '#1d6a5f'} strokeWidth={s.dash ? 1.3 : 1.8} strokeDasharray={s.dash || undefined} />
          <text x={26} y={pT-6} fontSize={10} fill="#555">{s.label}</text>
        </g>
      ))}

      {/* tooltip */}
      {hi !== null && (
        <g>
          <rect x={tipBx} y={pT + 4} width={TIP_W} height={TIP_H} rx={4}
            fill="#1B2A4A" opacity={0.92} />
          <text x={tipBx + 8} y={pT + 17} fontSize={10} fill="#fff" fontWeight="600">{(tipLabels || labels)[hi]}</text>
          {series.map((s, si) => (
            <text key={si} x={tipBx + 8} y={pT + 17 + (si + 1) * 18} fontSize={10} fill={s.dash ? '#C2A878' : '#fff'}>
              {s.label}: {(tipFmt || yFmt) ? (tipFmt || yFmt)(s.data[hi]) : s.data[hi]}
            </text>
          ))}
        </g>
      )}
    </svg>
  );
}

function IntaktLineChart({ monthMap, fgArKey }) {
  const pts = FY_ORDER.map(m => {
    const r  = monthMap[m];
    const fg = monthMap[fgArKey(m)];
    return { val: r ? r[1] : 0, fg: fg ? fg[1] : 0 };
  });
  const yFmt  = v => v >= 1e6 ? (v/1e6).toFixed(1)+'M' : v >= 1e3 ? Math.round(v/1e3)+'k' : '';
  const tipFmt = v => v > 0 ? Math.round(v/1000).toLocaleString('sv-SE') + ' tkr' : '–';
  return (
    <LineChart
      labels={MONTH_LABELS}
      yFmt={yFmt}
      tipFmt={tipFmt}
      series={[
        { label: RA_LBL, data: pts.map(p => p.val),  color: '#1B2A4A' },
        { label: 'Fg år',    data: pts.map(p => p.fg),   color: '#9E8FB0', dash: '5 3' },
      ]}
    />
  );
}

function BelaggningLineChart({ monthMap, fgArKey }) {
  const pts = FY_ORDER.map(m => {
    const r  = monthMap[m];
    const fg = monthMap[fgArKey(m)];
    return {
      val: r  ? Math.round((r[8]  || 0) * 100) : 0,
      fg:  fg ? Math.round((fg[8] || 0) * 100) : 0,
    };
  });
  return (
    <LineChart
      labels={MONTH_LABELS}
      yFmt={v => Math.round(v) + '%'}
      series={[
        { label: RA_LBL, data: pts.map(p => p.val), color: '#1D9E75' },
        { label: 'Fg år',    data: pts.map(p => p.fg),  color: '#9E8FB0', dash: '5 3' },
      ]}
    />
  );
}

// ── VECKO-HJÄLPARE ───────────────────────────────────────────────
function fmtVecka(yrWeek) { return "V" + (yrWeek % 100); }
function fgArWeekKey(yrWeek) { return yrWeek - 100; }
function yrWeekOf(d) {
  const dow = (d.getDay() + 6) % 7;
  const thu = new Date(d);
  thu.setDate(d.getDate() - dow + 3);
  const yr = thu.getFullYear();
  const jan4 = new Date(yr, 0, 4);
  const wk = 1 + Math.round(((thu - jan4) / 86400000 - 3 + (jan4.getDay() + 6) % 7) / 7);
  return yr * 100 + wk;
}
function todayYrWeek() { return yrWeekOf(new Date()); }
// Innevarande RÅ:s veckor (V27 → V26) — dynamiskt, hanterar 53-veckorsår
const FY_WEEK_KEYS = fyWeekKeys(todayYrWeek());

function VeckaIntaktChart({ weekMap, weeks = FY_WEEK_KEYS, enhetIdx = 1 }) {
  const pts = weeks.map(k => ({
    val: weekMap[k] ? weekMap[k][enhetIdx] : 0,
    fg:  weekMap[fgArWeekKey(k)] ? weekMap[fgArWeekKey(k)][enhetIdx] : 0,
  }));
  const step = weeks.length <= 13 ? 1 : weeks.length <= 26 ? 2 : 4;
  const xLabels   = weeks.map((k, i) => i % step === 0 ? fmtVecka(k) : '');
  const tipLabels = weeks.map(k => fmtVecka(k));
  const yFmt  = v => v >= 1e6 ? (v/1e6).toFixed(1)+'M' : v >= 1e3 ? Math.round(v/1e3)+'k' : '';
  const tipFmt = v => v > 0 ? Math.round(v/1000).toLocaleString('sv-SE') + ' tkr' : '–';
  return (
    <LineChart labels={xLabels} tipLabels={tipLabels} yFmt={yFmt} tipFmt={tipFmt}
      series={[
        { label: RA_LBL, data: pts.map(p => p.val), color: '#1B2A4A' },
        { label: 'Fg år',    data: pts.map(p => p.fg),  color: '#9E8FB0', dash: '5 3' },
      ]}
    />
  );
}

function VeckaBelaggningChart({ weekMap, weeks = FY_WEEK_KEYS }) {
  const pts = weeks.map(k => ({
    val: weekMap[k] ? Math.round((weekMap[k][2] || 0) * 100) : 0,
    fg:  weekMap[fgArWeekKey(k)] ? Math.round((weekMap[fgArWeekKey(k)][2] || 0) * 100) : 0,
  }));
  const step = weeks.length <= 13 ? 1 : weeks.length <= 26 ? 2 : 4;
  const xLabels   = weeks.map((k, i) => i % step === 0 ? fmtVecka(k) : '');
  const tipLabels = weeks.map(k => fmtVecka(k));
  return (
    <LineChart labels={xLabels} tipLabels={tipLabels} yFmt={v => Math.round(v) + '%'}
      series={[
        { label: RA_LBL, data: pts.map(p => p.val), color: '#1D9E75' },
        { label: 'Fg år',    data: pts.map(p => p.fg),  color: '#9E8FB0', dash: '5 3' },
      ]}
    />
  );
}

// ── GÄSTNÄTTER LINJEDIAGRAM (Dash) ────────────────────────────────
// Växlare Hotell/Förmedling/Total. Visbook saknar gästantal på förmedlings-
// objekt (Bokade gäster = 0 på stugor/lägenheter) → förmedling visas som
// bokade objekt per natt och total som belagda enheter (rum + objekt),
// samma konvention som Beläggning-fliken.
const GN_VYER = {
  hotell: { label: 'Hotell',     titel: 'Bokade gästnätter — 4 veckors vy',            enhet: 'gäster' },
  form:   { label: 'Förmedling', titel: 'Bokade objektnätter (förmedling) — 4 veckors vy', enhet: 'objekt' },
  total:  { label: 'Total',      titel: 'Belagda enheter (rum + objekt) — 4 veckors vy',   enhet: 'enheter' },
};

function GastnatterHistogram({ belaggningDays, belaggningLastYear, todayStr }) {
  const [vy, setVy] = useState('hotell'); // 'hotell' | 'form' | 'total'
  const [bel, setBel] = useState(null);   // beläggnings-cachen (rumRum/formObj per dag)
  useEffect(() => { getBelaggning().then(setBel).catch(() => setBel({ byDatum: {} })); }, []);

  if (!belaggningDays || belaggningDays.length < 2) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 32, color: '#888' }}>
        Hämtar bokningsdata...
      </div>
    );
  }

  const ti = todayStr ? Math.max(belaggningDays.findIndex(d => d.datum === todayStr), 0) : 7;

  function isoWk(dateStr) {
    const d = new Date(dateStr + 'T00:00:00Z');
    const dow = (d.getUTCDay() + 6) % 7;
    d.setUTCDate(d.getUTCDate() + 3 - dow);
    const yr = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil(((d - yr) / 86400000 + 1) / 7);
  }

  const DAYS_SV_FULL = ['Sön','Mån','Tis','Ons','Tor','Fre','Lör'];

  // Värde per dag givet vald vy. Hotell = gästnätter från bokningscachen
  // (oförändrat mot tidigare); form/total läser beläggnings-cachen på datum.
  const byDatum = bel?.byDatum || {};
  const valOf = (d) => {
    if (vy === 'hotell') return d.bokadeGaster || 0;
    const rec = byDatum[d.datum];
    if (vy === 'form') return rec ? rec.formObj : 0;
    return rec ? rec.rumRum + rec.formObj : (d.bokadeRum || 0); // total: enheter
  };
  const currData = belaggningDays.map(valOf);
  const fgData   = (belaggningLastYear || []).map(valOf);
  const hasFg    = fgData.some(v => v > 0);

  // X-etiketter: veckobeteckning vid varje måndag, annars tomt
  const xLabels = belaggningDays.map((d, i) => {
    const dateObj = new Date(d.datum + 'T00:00:00Z');
    const dow = dateObj.getUTCDay();
    if (i === ti) return `${dateObj.getUTCDate()}/${dateObj.getUTCMonth()+1}`;
    if (dow === 1) return `V${isoWk(d.datum)}`;
    return '';
  });

  const tipLabels = belaggningDays.map(d => {
    const dateObj = new Date(d.datum + 'T00:00:00Z');
    return `${DAYS_SV_FULL[dateObj.getUTCDay()]} ${dateObj.getUTCDate()}/${dateObj.getUTCMonth()+1}`;
  });

  const vyDef = GN_VYER[vy];
  const tipFmt = v => v > 0 ? `${v} ${vyDef.enhet}` : '–';

  const chip = (id) => (
    <button key={id} onClick={() => setVy(id)} style={{
      fontSize: 12, fontWeight: 600, padding: '4px 12px', borderRadius: 999,
      border: '1px solid ' + (vy === id ? '#1B2A4A' : 'var(--border)'),
      background: vy === id ? '#1B2A4A' : 'transparent', color: vy === id ? '#fff' : 'var(--text-secondary)',
      cursor: 'pointer', whiteSpace: 'nowrap',
    }}>{GN_VYER[id].label}</button>
  );

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
        <div className="card-title">{vyDef.titel}</div>
        <div style={{ display: 'flex', gap: 6 }}>{Object.keys(GN_VYER).map(chip)}</div>
      </div>
      <LineChart
        labels={xLabels}
        tipLabels={tipLabels}
        yFmt={v => Math.round(v) + ''}
        tipFmt={tipFmt}
        todayIdx={ti}
        series={[
          { label: RA_LBL, data: currData, color: '#1B2A4A' },
          ...(hasFg ? [{ label: 'Fg år', data: fgData, color: '#9E8FB0', dash: '5 3' }] : []),
        ]}
      />
      <div className="note" style={{ marginTop: 6 }}>
        {vy === 'hotell'
          ? 'Källa: Visbook — 7 dagar bakåt · 3 veckor framåt · fg år = samma period föregående år'
          : vy === 'form'
            ? 'Källa: Visbook — bokade förmedlingsobjekt (stugor/lägenheter) per natt · Visbook saknar gästantal på förmedling'
            : 'Källa: Visbook — belagda enheter per natt (hotellrum + förmedlingsobjekt) · fg år = samma period föregående år'}
      </div>
    </div>
  );
}

// ── BOKNINGSLÄGE FRAMÅT (planering): bokade rum/natt nu vs fjolår ──
function BokningslagePlan({ planDays, planDaysLastYear }) {
  if (!planDays || planDays.length < 2) {
    return <div className="card" style={{ textAlign: 'center', padding: 32, color: '#888' }}>Hämtar bokningsläge…</div>;
  }
  const DAYS_SV = ['Sön', 'Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör'];
  const curr  = planDays.map(d => d.bokadeRum || 0);
  const fg    = (planDaysLastYear || []).map(d => d.bokadeRum || 0);
  const hasFg = fg.some(v => v > 0);

  // Veckodag + datum under varje punkt (tvåradigt)
  const xLabels = planDays.map((d) => {
    const dt = new Date(d.datum + 'T00:00:00Z');
    return `${DAYS_SV[dt.getUTCDay()]}\n${dt.getUTCDate()}/${dt.getUTCMonth() + 1}`;
  });
  const tipLabels = planDays.map((d, i) => {
    const dt = new Date(d.datum + 'T00:00:00Z');
    const cur = `${DAYS_SV[dt.getUTCDay()]} ${dt.getUTCDate()}/${dt.getUTCMonth() + 1}`;
    const ly = planDaysLastYear && planDaysLastYear[i];
    if (ly) {
      const lyd = new Date(ly.datum + 'T00:00:00Z');
      return `${cur} · fjol ${lyd.getUTCDate()}/${lyd.getUTCMonth() + 1}`;
    }
    return cur;
  });

  return (
    <div className="card">
      <div className="card-title">Bokningsläge — kommande 4 veckor</div>
      <p style={{ fontSize: 12.5, color: 'var(--text-secondary)', margin: '0 0 6px' }}>
        Bokade rum per natt <strong>just nu</strong> (på böckerna) — skuggan visar fjolårets faktiska beläggning på samma veckodag.
      </p>
      <LineChart
        labels={xLabels}
        tipLabels={tipLabels}
        yFmt={v => Math.round(v) + ''}
        tipFmt={v => v > 0 ? v + ' rum' : '–'}
        todayIdx={0}
        series={[
          { label: 'I år (på böckerna)', data: curr, color: '#1B2A4A' },
          ...(hasFg ? [{ label: 'Fg år', data: fg, color: '#9E8FB0', dash: '5 3' }] : []),
        ]}
      />
      <div className="note" style={{ marginTop: 6 }}>Källa: Visbook — bokade rum/natt nu · fg år = faktisk beläggning samma veckodag (−364 dgr)</div>
    </div>
  );
}

// ── HOTELL DAGLIG FÖRSÄLJNING ─────────────────────────────────────
function HotellDagligForsaljning({ daily }) {
  const DAYS_SV = ['Sön', 'Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör'];

  function utcDateStr(d) {
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
  }

  function mondayOf(dateStr) {
    const d = new Date(dateStr + 'T00:00:00Z');
    const diff = (d.getUTCDay() + 6) % 7;
    d.setUTCDate(d.getUTCDate() - diff);
    return utcDateStr(d);
  }

  function isoWeekNr(dateStr) {
    const d = new Date(dateStr + 'T00:00:00Z');
    const dow = (d.getUTCDay() + 6) % 7;
    d.setUTCDate(d.getUTCDate() + 3 - dow);
    const yr = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil(((d - yr) / 86400000 + 1) / 7);
  }

  if (!daily || daily.length === 0) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 32, color: '#888' }}>
        Hämtar daglig data...
      </div>
    );
  }

  const getHotell = d => (d.hotellLogi || 0) + (d.hotellRest || 0) + (d.hotellOvrigt || 0);

  const last7       = daily.slice(0, 7).reverse();
  const todayStr    = daily[0].datum;
  const thisMonday  = mondayOf(todayStr);
  const lastMonday  = (() => { const d = new Date(thisMonday + 'T00:00:00Z'); d.setUTCDate(d.getUTCDate() - 7); return utcDateStr(d); })();
  const nextMonday  = (() => { const d = new Date(thisMonday + 'T00:00:00Z'); d.setUTCDate(d.getUTCDate() + 7); return utcDateStr(d); })();

  const thisWeekSum = daily.filter(d => d.datum >= thisMonday && d.datum < nextMonday).reduce((s, d) => s + getHotell(d), 0);
  const lastWeekSum = daily.filter(d => d.datum >= lastMonday && d.datum < thisMonday).reduce((s, d) => s + getHotell(d), 0);
  const weekChange  = lastWeekSum > 0 ? (thisWeekSum - lastWeekSum) / lastWeekSum : null;
  const thisWeekNr  = isoWeekNr(thisMonday);
  const lastWeekNr  = isoWeekNr(lastMonday);

  const maxVal = Math.max(...last7.map(d => getHotell(d)), 1);
  const W = 480, H = 120;
  const CT = 8, BH = 72, LY = 94, DY = 108;
  const padL = 16, padR = 16;
  const gW = (W - padL - padR) / last7.length;

  const C_LOGI = '#1B2A4A';
  const C_REST = '#1D9E75';
  const C_OVR  = '#E0A458';

  return (
    <div className="card">
      <div className="card-title">Daglig försäljning — Hotell</div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
        <div style={{ flex: 1 }}>
          <div className="metric-label">Denna vecka (V{thisWeekNr})</div>
          <div className="metric-value">{tkr(thisWeekSum)}</div>
          <div className="metric-sub">Hittills under veckan</div>
        </div>
        <div style={{ flex: 1 }}>
          <div className="metric-label">Förra veckan (V{lastWeekNr})</div>
          <div className="metric-value" style={{ color: '#555' }}>{lastWeekSum > 0 ? tkr(lastWeekSum) : '–'}</div>
          {weekChange !== null && (
            <div className="metric-sub" style={{ color: weekChange >= 0 ? '#1D9E75' : '#D85A30', fontWeight: 500 }}>
              {weekChange >= 0 ? '+' : ''}{Math.round(weekChange * 100)}%
            </div>
          )}
        </div>
      </div>

      <div style={{ borderTop: '1px solid #eee', paddingTop: 12 }}>
        <div style={{ fontSize: 11, color: '#999', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>
          Senaste 7 dagarna
        </div>
        <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
          {last7.map((d, i) => {
            const logi  = d.hotellLogi   || 0;
            const rest  = d.hotellRest   || 0;
            const ovrigt = d.hotellOvrigt || 0;
            const total = logi + rest + ovrigt;
            const isLast = i === last7.length - 1;

            const totalH = total > 500 ? Math.max(Math.round((total / maxVal) * BH), 3) : 2;
            const logiH  = total > 0 ? Math.round((logi  / maxVal) * BH) : 0;
            const restH  = total > 0 ? Math.round((rest  / maxVal) * BH) : 0;
            const ovriH  = totalH - logiH - restH;

            const cx  = padL + (i + 0.5) * gW;
            const bw  = Math.min(gW * 0.65, 50);
            const bx  = cx - bw / 2;
            const bot = CT + BH;

            const day  = DAYS_SV[new Date(d.datum + 'T00:00:00').getDay()];
            const date = new Date(d.datum + 'T00:00:00').getDate() + '/' + (new Date(d.datum + 'T00:00:00').getMonth() + 1);
            const lbl  = total > 500 ? Math.round(total / 1000) + 'k' : '–';
            const op   = isLast ? 1 : 0.75;

            return (
              <g key={i}>
                {logiH > 0 && <rect x={bx} y={bot - logiH} width={bw} height={logiH} rx={total <= 500 ? 1 : 0} fill={C_LOGI} opacity={op} />}
                {restH > 0 && <rect x={bx} y={bot - logiH - restH} width={bw} height={restH} fill={C_REST} opacity={op} />}
                {ovriH > 0 && <rect x={bx} y={bot - logiH - restH - ovriH} width={bw} height={ovriH} rx={2} fill={C_OVR} opacity={op} />}
                {total <= 500 && <rect x={bx} y={bot - 2} width={bw} height={2} rx={1} fill="#ddd" />}
                <text x={cx} y={bot - totalH - 3} textAnchor="middle" fontSize={9} fill={total > 500 ? '#444' : '#ccc'}>{lbl}</text>
                <text x={cx} y={LY} textAnchor="middle" fontSize={10} fontWeight={isLast ? '600' : '400'} fill={isLast ? '#1B2A4A' : '#666'}>{day}</text>
                <text x={cx} y={DY} textAnchor="middle" fontSize={9} fill="#aaa">{date}</text>
              </g>
            );
          })}
        </svg>
      </div>

      <div style={{ display: 'flex', gap: 14, marginTop: 6, fontSize: 10.5, color: '#666' }}>
        {[[C_LOGI,'Logi'],[C_REST,'Restaurangen'],[C_OVR,'Förmedling/Akt.']].map(([c, lbl]) => (
          <span key={lbl}>
            <span style={{ display: 'inline-block', width: 9, height: 9, background: c, borderRadius: 2, marginRight: 4, verticalAlign: 'middle' }} />
            {lbl}
          </span>
        ))}
      </div>
      <div className="note" style={{ marginTop: 6 }}>Källa: Qlik — Hotell</div>
    </div>
  );
}

// ── HOTELL DAGLIG AFFÄRSDATA ──────────────────────────────────────
function HotellDagligAffar({ rows, ytd, weeklyRows, weeklyLoading, daily }) {
  const [period, setPeriod] = useState('idag');

  const FY_MONTHS = fyMonthKeys(); // innevarande RÅ, dynamiskt
  const MN = ['','Jan','Feb','Mar','Apr','Maj','Jun','Jul','Aug','Sep','Okt','Nov','Dec'];

  function fgArKey(m) {
    const n = parseInt(m);
    return String(Math.floor(n / 100) - 1) + String(n % 100).padStart(2, '0');
  }

  // Dagens och gårdagens datum (lokal tid = svensk tid för datumsökning)
  const todayStr = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  })();
  const yesterdayStr = (() => {
    const d = new Date(); d.setDate(d.getDate() - 1);
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  })();

  // Aktuell kalendermånad och vecka (UTC för att undvika DST)
  const now = new Date();
  const thisYm = now.getUTCFullYear() * 100 + (now.getUTCMonth() + 1);
  const prevYm = now.getUTCMonth() === 0
    ? (now.getUTCFullYear() - 1) * 100 + 12
    : now.getUTCFullYear() * 100 + now.getUTCMonth();

  const thisWeekKey = (() => {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
    const dow = (d.getUTCDay() + 6) % 7;
    d.setUTCDate(d.getUTCDate() + 3 - dow);
    const yr = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return d.getUTCFullYear() * 100 + Math.ceil(((d - yr) / 86400000 + 1) / 7);
  })();
  const prevWeekKey = thisWeekKey % 100 > 1
    ? thisWeekKey - 1
    : (Math.floor(thisWeekKey / 100) - 1) * 100 + 52;

  // ── DATA-KONVERTERARE ──────────────────────────────────────────
  const EMPTY = {
    tot: 0, logi: 0, rest: 0, ovrigt: 0, tb1: 0, tb1L: 0, tb1R: 0, tb1O: 0,
    brass: 0, knallen: 0, sport: 0, stug: 0,
    timHotell: 0, timRest: 0, timBrass: 0, timKnallen: 0, timSport: 0, timStug: 0,
    timRestFrukost: 0, timRestLunch: 0, revRestFrukost: 0, revRestLunch: 0,
    lonLogi: 0, lonRest: 0,
  };

  function mRowToData(r) {
    if (!r) return EMPTY;
    const logi = r[10]||0, rest = r[11]||0, ovrigt = r[12]||0;
    const tb1L = r[13]||0, tb1R = r[14]||0, tb1O   = r[15]||0;
    const brass = r[2]||0, knallen = r[3]||0, sport = r[4]||0, stug = r[5]||0;
    const timHotell = r[16]||0, timRest = r[17]||0, timBrass = r[18]||0;
    const timKnallen = r[19]||0, timSport = r[20]||0, timStug = r[21]||0;
    const timRestFrukost = r[22]||0, timRestLunch = r[23]||0;
    const revRestFrukost = r[24]||0, revRestLunch = r[25]||0;
    return { tot: logi+rest+ovrigt, logi, rest, ovrigt, tb1: tb1L+tb1R+tb1O, tb1L, tb1R, tb1O,
             brass, knallen, sport, stug, timHotell, timRest, timBrass, timKnallen, timSport, timStug,
             timRestFrukost, timRestLunch, revRestFrukost, revRestLunch,
             lonLogi: r[42]||0, lonRest: r[43]||0 };
  }

  function dRowToData(d) {
    if (!d) return EMPTY;
    return { tot: (d.hotellLogi||0)+(d.hotellRest||0)+(d.hotellOvrigt||0),
             logi: d.hotellLogi||0, rest: d.hotellRest||0, ovrigt: d.hotellOvrigt||0,
             tb1: (d.tb1Logi||0)+(d.tb1Rest||0)+(d.tb1Ovrigt||0),
             tb1L: d.tb1Logi||0, tb1R: d.tb1Rest||0, tb1O: d.tb1Ovrigt||0,
             brass: d.brasserie||0, knallen: d.knallen||0, sport: d.sportcenter||0, stug: 0,
             timHotell: d.timHotell||0, timRest: d.timRest||0,
             timBrass: 0, timKnallen: 0, timSport: 0, timStug: 0,
             timRestFrukost: d.timRestFrukost||0, timRestLunch: d.timRestLunch||0,
             revRestFrukost: d.revRestFrukost||0, revRestLunch: d.revRestLunch||0,
             lonLogi: d.lonHotellLogi||0, lonRest: d.lonHotellRest||0 };
  }

  function wRowToData(r) {
    if (!r) return EMPTY;
    return { tot: r[1]||0, logi: r[4]||0, rest: r[5]||0, ovrigt: r[6]||0,
             tb1: r[3]||0, tb1L: r[7]||0, tb1R: r[8]||0, tb1O:   r[9]||0,
             timHotell: r[10]||0, timRest: r[11]||0, timBrass: r[12]||0,
             timKnallen: r[13]||0, timSport: r[14]||0, timStug: r[15]||0,
             brass: r[16]||0, knallen: r[17]||0, sport: r[18]||0, stug: r[19]||0,
             timRestFrukost: r[20]||0, timRestLunch: r[21]||0,
             revRestFrukost: r[22]||0, revRestLunch: r[23]||0,
             lonLogi: 0, lonRest: 0 };
  }

  // ── PERIOD-DATA ────────────────────────────────────────────────
  function getPeriodData() {
    if (period === 'idag' || period === 'igar') {
      const dateStr = period === 'idag' ? todayStr : yesterdayStr;
      return { c: dRowToData(daily?.find(r => r.datum === dateStr)), f: EMPTY };
    }
    if (period === 'ytd') {
      const fyFgSet = new Set(FY_MONTHS.map(fgArKey));
      const c = {
        logi: ytd.hotellLogi||0, rest: ytd.hotellrestaurangen||0, ovrigt: ytd.hotellOvrigt||0,
        tb1L: ytd.tb1HotellLogi||0, tb1R: ytd.tb1Hotellrest||0, tb1O: ytd.tb1HotellOvrigt||0,
        brass: ytd.brasserieKerstin||0, knallen: ytd.knallen||0,
        sport: ytd.sportcenter||0, stug: ytd.stugservice||0,
        timHotell: ytd.timHotell||0, timRest: ytd.timRest||0, timBrass: ytd.timBrass||0,
        timKnallen: ytd.timKnallen||0, timSport: ytd.timSport||0, timStug: ytd.timStug||0,
        timRestFrukost: ytd.timRestFrukost||0, timRestLunch: ytd.timRestLunch||0,
        revRestFrukost: ytd.revRestFrukost||0, revRestLunch: ytd.revRestLunch||0,
        lonLogi: ytd.lonHotellLogi||0, lonRest: ytd.lonHotellrest||0,
      };
      c.tot = c.logi + c.rest + c.ovrigt;
      c.tb1 = c.tb1L + c.tb1R + c.tb1O;
      const f = { logi: 0, rest: 0, ovrigt: 0, tb1L: 0, tb1R: 0, tb1O: 0,
                  brass: 0, knallen: 0, sport: 0, stug: 0,
                  timHotell: 0, timRest: 0, timBrass: 0, timKnallen: 0, timSport: 0, timStug: 0,
                  timRestFrukost: 0, timRestLunch: 0, revRestFrukost: 0, revRestLunch: 0,
                  lonLogi: 0, lonRest: 0 };
      rows.filter(r => fyFgSet.has(String(r[0]))).forEach(r => {
        f.logi+=r[10]||0; f.rest+=r[11]||0; f.ovrigt+=r[12]||0;
        f.tb1L+=r[13]||0; f.tb1R+=r[14]||0; f.tb1O  +=r[15]||0;
        f.brass+=r[2]||0; f.knallen+=r[3]||0; f.sport+=r[4]||0; f.stug+=r[5]||0;
        f.timHotell+=r[16]||0; f.timRest+=r[17]||0; f.timBrass+=r[18]||0;
        f.timKnallen+=r[19]||0; f.timSport+=r[20]||0; f.timStug+=r[21]||0;
        f.timRestFrukost+=r[22]||0; f.timRestLunch+=r[23]||0;
        f.revRestFrukost+=r[24]||0; f.revRestLunch+=r[25]||0;
        f.lonLogi+=r[42]||0; f.lonRest+=r[43]||0;
      });
      f.tot = f.logi+f.rest+f.ovrigt; f.tb1 = f.tb1L+f.tb1R+f.tb1O;
      return { c, f };
    }
    if (period === 'denna_manad' || period === 'forra_manad') {
      const ym = period === 'denna_manad' ? thisYm : prevYm;
      return {
        c: mRowToData(rows.find(r => String(r[0]) === String(ym))),
        f: mRowToData(rows.find(r => String(r[0]) === fgArKey(String(ym)))),
      };
    }
    const wk = period === 'denna_vecka' ? thisWeekKey : prevWeekKey;
    return {
      c: wRowToData(weeklyRows?.find(r => r[0] === wk)),
      f: wRowToData(weeklyRows?.find(r => r[0] === wk - 100)),
    };
  }

  const isWeekPeriod   = period === 'denna_vecka' || period === 'forra_vecka';
  const isDailyPeriod  = period === 'idag' || period === 'igar';
  const PERIODS = [
    { id: 'idag',        label: 'Idag' },
    { id: 'igar',        label: 'Igår' },
    { id: 'denna_vecka', label: `V${thisWeekKey % 100}` },
    { id: 'forra_vecka', label: `V${prevWeekKey % 100}` },
    { id: 'denna_manad', label: MN[thisYm % 100] },
    { id: 'forra_manad', label: MN[prevYm % 100] },
    { id: 'ytd',         label: 'YTD' },
  ];

  const periodLabel = (() => {
    if (period === 'idag')        return 'Idag (' + todayStr.slice(5).replace('-', '/') + ')';
    if (period === 'igar')        return 'Igår (' + yesterdayStr.slice(5).replace('-', '/') + ')';
    if (period === 'ytd')         return `YTD (${RA_LBL})`;
    if (period === 'denna_manad') return MN[thisYm % 100] + ' ' + Math.floor(thisYm / 100);
    if (period === 'forra_manad') return MN[prevYm % 100] + ' ' + Math.floor(prevYm / 100);
    if (period === 'denna_vecka') return `Vecka ${thisWeekKey % 100}`;
    return `Vecka ${prevWeekKey % 100}`;
  })();

  // ── CELL & GRID ────────────────────────────────────────────────
  function chg(curr, prev) {
    if (!prev) return null;
    const v = (curr - prev) / Math.abs(prev);
    return { s: (v >= 0 ? '+' : '') + Math.round(v * 100) + '%', up: v >= 0 };
  }

  function Cell({ v, fv }) {
    const p = chg(v, fv);
    return (
      <div style={{ textAlign: 'right', padding: '8px 0 4px' }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: v < 0 ? '#D85A30' : '#1B2A4A', lineHeight: 1.1 }}>
          {v !== 0 ? tkr(v) : '–'}
        </div>
        <div style={{ fontSize: 11, color: '#aaa', marginTop: 2 }}>{fv !== 0 ? tkr(fv) : '–'}</div>
        {p && <div style={{ fontSize: 11, fontWeight: 600, color: p.up ? '#1D9E75' : '#D85A30', marginTop: 1 }}>{p.s}</div>}
      </div>
    );
  }

  const grid = { display: 'grid', gridTemplateColumns: '88px repeat(4, 1fr)', columnGap: 10 };
  const sep  = { borderBottom: '1px solid #f0eeeb' };
  const COLS = ['Hotellet', 'Hotell Logi', 'Restaurangen', 'Förmedling'];

  const btnStyle = (active) => ({
    padding: '4px 10px', fontSize: 12, fontFamily: 'system-ui', fontWeight: 500,
    background: active ? '#1B2A4A' : 'none', color: active ? '#fff' : '#888',
    border: '1px solid', borderColor: active ? '#1B2A4A' : '#ddd',
    borderRadius: 4, cursor: 'pointer',
  });

  if (isWeekPeriod && weeklyLoading) {
    return (
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div className="card-title" style={{ margin: 0 }}>Hotellet — affärsdata</div>
          <div style={{ display: 'flex', gap: 4 }}>
            {PERIODS.map(p => <button key={p.id} onClick={() => setPeriod(p.id)} style={btnStyle(period === p.id)}>{p.label}</button>)}
          </div>
        </div>
        <div style={{ textAlign: 'center', padding: 32, color: '#888' }}>Hämtar veckodata från Qlik...</div>
      </div>
    );
  }

  const { c, f } = getPeriodData();
  const dataRows = [
    { label: 'INTÄKTER', vals: [[c.tot,f.tot],[c.logi,f.logi],[c.rest,f.rest],[c.ovrigt,f.ovrigt]] },
    { label: 'KOSTNADER', vals: [
      [-(c.tot-c.tb1),-(f.tot-f.tb1)], [-(c.logi-c.tb1L),-(f.logi-f.tb1L)],
      [-(c.rest-c.tb1R),-(f.rest-f.tb1R)], [-(c.ovrigt-c.tb1O),-(f.ovrigt-f.tb1O)],
    ]},
    { label: 'TB1', vals: [[c.tb1,f.tb1],[c.tb1L,f.tb1L],[c.tb1R,f.tb1R],[c.tb1O,f.tb1O]] },
  ];
  const margData = [
    [c.tb1,c.tot,f.tb1,f.tot], [c.tb1L,c.logi,f.tb1L,f.logi],
    [c.tb1R,c.rest,f.tb1R,f.rest], [c.tb1O,c.ovrigt,f.tb1O,f.ovrigt],
  ];

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <div className="card-title" style={{ margin: 0 }}>Hotellet — affärsdata</div>
          <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{periodLabel}</div>
        </div>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {PERIODS.map(p => <button key={p.id} onClick={() => setPeriod(p.id)} style={btnStyle(period === p.id)}>{p.label}</button>)}
        </div>
      </div>

      {/* Mobil: tabellen scrollar i kortet (affars-scroll) i stället för att spränga sidan */}
      <div className="affars-scroll"><div className="affars-inner">
      <div style={grid}>
        <div />
        {COLS.map(h => (
          <div key={h} style={{ fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: 0.7, textAlign: 'right', paddingBottom: 6, borderBottom: '2px solid #1B2A4A' }}>{h}</div>
        ))}
      </div>

      {dataRows.map((row, ri) => (
        <div key={ri} style={{ ...grid, ...sep }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#555', letterSpacing: 0.5, paddingTop: 10 }}>{row.label}</div>
          {row.vals.map(([v, fv], ci) => <Cell key={ci} v={v} fv={fv} />)}
        </div>
      ))}

      <div style={{ ...grid, marginTop: 6 }}>
        <div style={{ fontSize: 11, color: '#888', paddingTop: 6 }}>TB1-marginal</div>
        {margData.map(([tb1, int, ftb1, fint], i) => {
          const m = int > 0 ? tb1 / int : 0, fm = fint > 0 ? ftb1 / fint : 0;
          return (
            <div key={i} style={{ textAlign: 'right', paddingTop: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: m >= 0.5 ? '#1D9E75' : m >= 0.25 ? '#E0A458' : '#D85A30' }}>
                {int > 0 ? Math.round(m * 100) + '%' : '–'}
              </span>
              {fm > 0 && <span style={{ fontSize: 11, color: '#aaa', marginLeft: 4 }}>({Math.round(fm * 100)}%)</span>}
            </div>
          );
        })}
      </div>
      </div></div>

      {/* ── TIMMAR & OMS/TIM ── */}
      <div style={{ marginTop: 20, borderTop: '1px solid #E0DED8', paddingTop: 14 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#555', letterSpacing: 0.5, marginBottom: 10 }}>ARBETADE TIMMAR & OMS/TIM</div>
        {/* auto-fit: två i bredd på desktop, staplade på mobil — ingen sidscroll */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 10 }}>

          {/* ── Hotell Logi ── */}
          {(() => {
            const tim = c.timHotell, rev = c.logi, ftim = f.timHotell, frev = f.logi;
            const omsPerTim  = tim  > 0 ? Math.round(rev  / tim)  : 0;
            const fomsPerTim = ftim > 0 ? Math.round(frev / ftim) : 0;
            const diff = omsPerTim > 0 && fomsPerTim > 0 ? omsPerTim - fomsPerTim : null;
            const pp = persPct(c.lonLogi, rev), fpp = persPct(f.lonLogi, frev);
            return (
              <div style={{ background: 'var(--panel)', borderRadius: 6, padding: '10px 14px' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#1B2A4A', marginBottom: 6 }}>Hotell Logi</div>
                {/* Rubriker */}
                <div style={{ display: 'grid', gridTemplateColumns: '72px repeat(4, 1fr)', gap: 4, paddingBottom: 5, borderBottom: '1px solid #ddd' }}>
                  <div />
                  {['Intäkt', 'Arbetad (h)', 'Oms. per (h)', 'Personal%'].map(h => (
                    <div key={h} style={{ fontSize: 9, color: '#aaa', textTransform: 'uppercase', letterSpacing: 0.5, textAlign: 'right' }}>{h}</div>
                  ))}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '72px repeat(4, 1fr)', gap: 4, padding: '5px 0', alignItems: 'center' }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#1B2A4A' }}>
                    Total
                    {ftim > 0 && <div style={{ fontSize: 9, color: '#bbb', fontWeight: 400 }}>fg: {Math.round(ftim).toLocaleString('sv-SE')} tim</div>}
                  </div>
                  {/* Intäkt */}
                  <div style={{ textAlign: 'right' }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#1B2A4A' }}>{rev > 0 ? Math.round(rev/1000).toLocaleString('sv-SE') : '–'}</span>
                    {rev > 0 && <span style={{ fontSize: 9, color: '#aaa', marginLeft: 1 }}>tkr</span>}
                    {frev > 0 && <div style={{ fontSize: 9, color: '#bbb' }}>fg: {Math.round(frev/1000).toLocaleString('sv-SE')} tkr</div>}
                  </div>
                  {/* Timmar */}
                  <div style={{ textAlign: 'right' }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#1B2A4A' }}>{tim > 0 ? Math.round(tim).toLocaleString('sv-SE') : '–'}</span>
                  </div>
                  {/* Kr/tim */}
                  <div style={{ textAlign: 'right' }}>
                    {omsPerTim > 0 ? (
                      <>
                        <span style={{ fontSize: 12, fontWeight: 600, color: '#1B2A4A' }}>{omsPerTim.toLocaleString('sv-SE')}</span>
                        {fomsPerTim > 0 && diff !== null && (
                          <div style={{ fontSize: 9, color: diff >= 0 ? '#1D9E75' : '#D85A30', fontWeight: 600 }}>
                            {diff >= 0 ? '+' : ''}{diff.toLocaleString('sv-SE')}
                          </div>
                        )}
                      </>
                    ) : <span style={{ fontSize: 11, color: '#ccc' }}>–</span>}
                  </div>
                  {/* Personal% */}
                  <div style={{ textAlign: 'right' }}>
                    {pp != null ? (
                      <>
                        <span style={{ fontSize: 12, fontWeight: 600, color: persColor(pp) }}>{pp}%</span>
                        {fpp != null && <div style={{ fontSize: 9, color: '#bbb' }}>fg: {fpp}%</div>}
                      </>
                    ) : <span style={{ fontSize: 11, color: '#ccc' }}>–</span>}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* ── Restaurangen (med Frukost/Lunch-split) ── */}
          <div style={{ background: 'var(--panel)', borderRadius: 6, padding: '10px 14px' }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#1B2A4A', marginBottom: 6 }}>Restaurangen</div>
            {/* Rubriker */}
            <div style={{ display: 'grid', gridTemplateColumns: '72px repeat(4, 1fr)', gap: 4, paddingBottom: 5, borderBottom: '1px solid #ddd' }}>
              <div />
              {['Intäkt', 'Arbetad (h)', 'Oms. per (h)', 'Personal%'].map(h => (
                <div key={h} style={{ fontSize: 9, color: '#aaa', textTransform: 'uppercase', letterSpacing: 0.5, textAlign: 'right' }}>{h}</div>
              ))}
            </div>
            {[
              { label: 'Total',        tim: c.timRest,        rev: c.rest,          ftim: f.timRest,        frev: f.rest,          lon: c.lonRest, flon: f.lonRest },
              { label: 'Frukost',      tim: c.timRestFrukost, rev: c.revRestFrukost,ftim: f.timRestFrukost, frev: f.revRestFrukost, lon: null, flon: null },
              { label: 'Lunch/Middag', tim: c.timRestLunch,   rev: c.revRestLunch,  ftim: f.timRestLunch,   frev: f.revRestLunch,   lon: null, flon: null },
            ].map(({ label, tim, rev, ftim, frev, lon, flon }, i) => {
              const omsPerTim  = tim  > 0 ? Math.round(rev  / tim)  : 0;
              const fomsPerTim = ftim > 0 ? Math.round(frev / ftim) : 0;
              const diff = omsPerTim > 0 && fomsPerTim > 0 ? omsPerTim - fomsPerTim : null;
              const pp = lon != null ? persPct(lon, rev) : null, fpp = flon != null ? persPct(flon, frev) : null;
              const fw = i === 0 ? 600 : 400;
              const fs = i === 0 ? 12 : 11;
              return (
                <div key={label} style={{ display: 'grid', gridTemplateColumns: '72px repeat(4, 1fr)', gap: 4,
                                          padding: '5px 0', borderTop: i > 0 ? '1px solid #ece9e4' : 'none', alignItems: 'center' }}>
                  <div style={{ fontSize: fs, fontWeight: fw, color: '#1B2A4A' }}>
                    {label}
                    {ftim > 0 && <div style={{ fontSize: 9, color: '#bbb', fontWeight: 400 }}>fg: {Math.round(ftim).toLocaleString('sv-SE')} tim</div>}
                  </div>
                  {/* Intäkt */}
                  <div style={{ textAlign: 'right' }}>
                    <span style={{ fontSize: fs, fontWeight: fw, color: '#1B2A4A' }}>{rev > 0 ? Math.round(rev/1000).toLocaleString('sv-SE') : '–'}</span>
                    {rev > 0 && <span style={{ fontSize: 9, color: '#aaa', marginLeft: 1 }}>tkr</span>}
                    {frev > 0 && <div style={{ fontSize: 9, color: '#bbb' }}>fg: {Math.round(frev/1000).toLocaleString('sv-SE')} tkr</div>}
                  </div>
                  {/* Timmar */}
                  <div style={{ textAlign: 'right' }}>
                    <span style={{ fontSize: fs, fontWeight: fw, color: '#1B2A4A' }}>{tim > 0 ? Math.round(tim).toLocaleString('sv-SE') : '–'}</span>
                  </div>
                  {/* Kr/tim */}
                  <div style={{ textAlign: 'right' }}>
                    {omsPerTim > 0 ? (
                      <>
                        <span style={{ fontSize: fs, fontWeight: fw, color: '#1B2A4A' }}>{omsPerTim.toLocaleString('sv-SE')}</span>
                        {fomsPerTim > 0 && diff !== null && (
                          <div style={{ fontSize: 9, color: diff >= 0 ? '#1D9E75' : '#D85A30', fontWeight: 600 }}>
                            {diff >= 0 ? '+' : ''}{diff.toLocaleString('sv-SE')}
                          </div>
                        )}
                      </>
                    ) : <span style={{ fontSize: 11, color: '#ccc' }}>–</span>}
                  </div>
                  {/* Personal% */}
                  <div style={{ textAlign: 'right' }}>
                    {pp != null ? (
                      <>
                        <span style={{ fontSize: fs, fontWeight: fw, color: persColor(pp) }}>{pp}%</span>
                        {fpp != null && <div style={{ fontSize: 9, color: '#bbb' }}>fg: {fpp}%</div>}
                      </>
                    ) : <span style={{ fontSize: 11, color: '#ccc' }}>–</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="note" style={{ marginTop: 12 }}>
        Källa: Qlik · {periodLabel}{!isDailyPeriod && ' · Fg år = samma period föregående räkenskapsår'} · Timmar via Personalkollen
      </div>
    </div>
  );
}

// ── HOTELL BLOCK ──────────────────────────────────────────────────
export default function HotellBlock({ data }) {
  const [sub, setSub]               = useState('reception');
  const [periodSub, setPeriodSub]   = useState('veckor');
  const [weeklyRows, setWeeklyRows] = useState(null);
  const [weeklyLoading, setWeeklyLoading] = useState(false);
  const [veckoHorisont, setVeckoHorisont] = useState('hela_ra');
  const [veckoEnhet, setVeckoEnhet]       = useState('hotellet');
  const [veckoFrom, setVeckoFrom]         = useState(''); // egen datumperiod (åsidosätter presets)
  const [veckoTo, setVeckoTo]             = useState('');
  const [roomStats, setRoomStats]         = useState(null);
  const [roomStatsLoading, setRoomStatsLoading] = useState(false);

  async function loadWeekly() {
    if (weeklyRows || weeklyLoading) return;
    setWeeklyLoading(true);
    try {
      const rows = await getWeeklyData();
      setWeeklyRows(rows);
    } catch (e) {
      console.error('Weekly load error:', e);
      setWeeklyRows([]);
    } finally {
      setWeeklyLoading(false);
    }
  }

  async function loadRoomStats() {
    if (roomStats || roomStatsLoading) return;
    setRoomStatsLoading(true);
    try {
      const rooms = await getRoomStats();
      setRoomStats(rooms);
    } catch (e) {
      console.error('Room stats error:', e);
      setRoomStats([]);
    } finally {
      setRoomStatsLoading(false);
    }
  }

  function handlePeriodTab(id) {
    setPeriodSub(id);
    if (id === 'veckor') loadWeekly();
  }

  const rows        = data?.monthly     || [];
  const daily       = data?.daily       || [];
  const bookings    = data?.bookings    || null;
  const newBookings = data?.newBookings || null;
  const ytd         = data?.ytd         || {};

  // YTD-KPI:er för Period & trend
  const hotellTot     = (ytd.hotellLogi || 0) + (ytd.hotellrestaurangen || 0) + (ytd.hotellOvrigt || 0);
  const hotellTB1     = (ytd.tb1HotellLogi || 0) + (ytd.tb1Hotellrest || 0);
  const hotellTG      = hotellTot > 0 ? hotellTB1 / hotellTot : 0;
  const belaggningYTD = (ytd.mojligaRumsnatter || 0) > 0 ? (ytd.bokadeRumsnatter || 0) / ytd.mojligaRumsnatter : 0;
  const gastnatterYTD = ytd.gastnatter || 0;
  const snittpris     = gastnatterYTD > 0 ? Math.round((ytd.hotellLogi || 0) / gastnatterYTD) : 0;

  // Fg år-jämförelse: arManad-karta för snabbuppslag
  const monthMap = {};
  rows.forEach(r => { monthMap[String(r[0])] = r; });
  function fgArKey(arManad) {
    const n = parseInt(arManad);
    return String(Math.floor(n / 100) - 1) + String(n % 100).padStart(2, '0');
  }

  const visibleRows = rows.filter(r => r[1] > 0 || r[8] > 0).slice(0, 24);

  const TABS = [
    { id: 'reception', label: 'Dash reception'  },
    { id: 'daglig',    label: 'Daglig översikt' },
    { id: 'period',    label: 'Period & trend'  },
    { id: 'planering', label: 'För planering'   },
    { id: 'norderi',   label: 'Nörderi'         },
  ];

  return (
    <div className="screen">
      <div className="header header-hotell">
        <div>
          <div className="header-title">Bruksvallsliden Hotell</div>
          <div className="header-name">Logi · Restaurang · Förmedling · Aktivitet</div>
        </div>
        <Clock />
      </div>
      <SubNav tabs={TABS} active={sub} onChange={(id) => {
        setSub(id);
        if (id === 'period' || id === 'daglig') loadWeekly();
        if (id === 'norderi') loadRoomStats();
      }} />

      {/* Målet ur budgeten — hotellets fyra resultatenheter tillsammans */}
      {(sub === 'reception' || sub === 'daglig') && (
        <MalKort enheter={['Hotell Logi', 'Hotell Förmedling', 'Hotell Aktivitet', 'Hotellrestaurangen']} />
      )}

      {/* ── DASH RECEPTION ── */}
      {sub === 'reception' && (
        <>
          <HotelDailyOverview bookings={bookings} monthly={rows} newBookings={newBookings} />
          <GastnatterHistogram belaggningDays={bookings?.belaggningDays} belaggningLastYear={bookings?.belaggningLastYear} todayStr={bookings?.todayStr} />
        </>
      )}

      {/* ── DAGLIG ÖVERSIKT ── */}
      {sub === 'daglig' && (
        <>
          <HotellDagligAffar rows={rows} ytd={ytd} daily={daily} weeklyRows={weeklyRows} weeklyLoading={weeklyLoading} />
          <HotellDagligForsaljning daily={daily} />
        </>
      )}

      {/* ── PERIOD & TREND ── */}
      {sub === 'period' && (
        <>
          {/* underflikar */}
          <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid #E0DED8', marginBottom: 16 }}>
            {[{ id: 'veckor', label: 'Veckor' }, { id: 'manad', label: 'Månad' }].map(t => (
              <button key={t.id} onClick={() => handlePeriodTab(t.id)} style={{
                padding: '8px 20px', fontSize: 13, fontFamily: 'system-ui', fontWeight: 500,
                background: 'none', border: 'none', borderBottom: periodSub === t.id ? '2px solid #1B2A4A' : '2px solid transparent',
                color: periodSub === t.id ? '#1B2A4A' : '#888', cursor: 'pointer', marginBottom: -1,
              }}>{t.label}</button>
            ))}
          </div>

          {/* ── VECKOR ── */}
          {periodSub === 'veckor' && (() => {
            if (weeklyLoading) return (
              <div className="card" style={{ textAlign: 'center', padding: 32, color: '#888' }}>Hämtar veckodata från Qlik...</div>
            );
            if (!weeklyRows) return null;
            const weekMap = {};
            weeklyRows.forEach(r => { weekMap[r[0]] = r; });

            // Tidshorisont — presets ur FY_WEEK_KEYS, eller egen datumperiod
            // (datumintervall → ISO-veckonycklar via måndags-iteration)
            const currWk = todayYrWeek();
            const customActive = !!(veckoFrom && veckoTo && veckoFrom <= veckoTo);
            let filteredWeeks;
            if (customActive) {
              const keys = [];
              const d = new Date(veckoFrom + 'T12:00:00');
              d.setDate(d.getDate() - ((d.getDay() + 6) % 7)); // backa till måndag
              const end = new Date(veckoTo + 'T12:00:00');
              let guard = 0;
              while (d <= end && guard++ < 160) { keys.push(yrWeekOf(d)); d.setDate(d.getDate() + 7); }
              filteredWeeks = keys;
            } else {
              const fyToDate = FY_WEEK_KEYS.filter(k => k <= currWk);
              filteredWeeks = veckoHorisont === 'senaste_3' ? fyToDate.slice(-13)
                            : veckoHorisont === 'senaste_6' ? fyToDate.slice(-26)
                            : FY_WEEK_KEYS;
            }
            const filteredSet = new Set(filteredWeeks);

            // Enhet — välj rätt kolumn för intäktschart
            const enhetIdx   = veckoEnhet === 'logi' ? 4 : veckoEnhet === 'rest' ? 5 : 1;
            const enhetLabel = veckoEnhet === 'logi' ? 'Hotell Logi' : veckoEnhet === 'rest' ? 'Restaurangen' : 'Hotellet';
            const enhetNote  = veckoEnhet === 'logi' ? 'Qlik — Hotell Logi'
                             : veckoEnhet === 'rest' ? 'Qlik — Hotellrestaurangen'
                             : 'Qlik — Hotell (Logi + Rest + Förm. + Akt.)';

            const tableRows = weeklyRows.filter(r => filteredSet.has(r[0]));

            const PILL = (active, onClick, label) => (
              <button key={label} onClick={onClick} style={{
                padding: '4px 12px', fontSize: 12, fontFamily: 'system-ui', fontWeight: 500,
                background: active ? '#1B2A4A' : '#F0EFE9',
                color: active ? '#fff' : '#555',
                border: 'none', borderRadius: 20, cursor: 'pointer', transition: 'background 0.15s',
              }}>{label}</button>
            );

            return (
              <>
                {/* Filter-rad */}
                <div style={{ display: 'flex', gap: 24, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
                  <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                    <span style={{ fontSize: 11, color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginRight: 4 }}>Period</span>
                    {[['hela_ra','Hela RÅ'],['senaste_6','Senaste 6 mån'],['senaste_3','Senaste 3 mån']].map(([id, lbl]) =>
                      PILL(veckoHorisont === id && !customActive, () => { setVeckoHorisont(id); setVeckoFrom(''); setVeckoTo(''); }, lbl)
                    )}
                    <input type="date" value={veckoFrom} onChange={(e) => setVeckoFrom(e.target.value)} aria-label="Från datum"
                      style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '3px 8px', fontSize: 11.5, fontFamily: 'system-ui,sans-serif', background: 'transparent', color: 'var(--text)', marginLeft: 6 }} />
                    <span style={{ color: '#888', fontSize: 11.5 }}>–</span>
                    <input type="date" value={veckoTo} onChange={(e) => setVeckoTo(e.target.value)} aria-label="Till datum"
                      style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '3px 8px', fontSize: 11.5, fontFamily: 'system-ui,sans-serif', background: 'transparent', color: 'var(--text)' }} />
                    {customActive && PILL(false, () => { setVeckoFrom(''); setVeckoTo(''); }, 'Rensa')}
                  </div>
                  <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                    <span style={{ fontSize: 11, color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginRight: 4 }}>Enhet</span>
                    {[['hotellet','Hotellet'],['logi','Hotell Logi'],['rest','Restaurangen']].map(([id, lbl]) =>
                      PILL(veckoEnhet === id, () => setVeckoEnhet(id), lbl)
                    )}
                  </div>
                </div>

                <div className="card">
                  <div className="card-title">Intäkter per vecka — {enhetLabel} · {customActive ? "vald period" : RA_LBL} vs fg år</div>
                  <VeckaIntaktChart weekMap={weekMap} weeks={filteredWeeks} enhetIdx={enhetIdx} />
                  <div className="note" style={{ marginTop: 8 }}>Källa: {enhetNote}</div>
                </div>
                <div className="card">
                  <div className="card-title">Beläggning % per vecka — Hotellet · {customActive ? "vald period" : RA_LBL} vs fg år</div>
                  <VeckaBelaggningChart weekMap={weekMap} weeks={filteredWeeks} />
                  <div className="note" style={{ marginTop: 8 }}>Källa: Qlik #BokadeRumsnätter / #MöjligaRumsnätter</div>
                </div>
                <div className="card">
                  <div className="card-title">Veckotabell — {enhetLabel}</div>
                  <div className="period-table">
                    <div className="period-header period-row-6">
                      <span>Vecka</span><span>Intäkt</span><span>Fg år</span><span>+%</span><span>TB1</span><span>Belägg.</span>
                    </div>
                    {tableRows.map((r, i) => {
                      const intVal = r[enhetIdx] || 0;
                      const fg     = weekMap[fgArWeekKey(r[0])];
                      const fgVal  = fg ? (fg[enhetIdx] || 0) : 0;
                      const diff   = fgVal > 0 ? (intVal - fgVal) / fgVal : null;
                      const dc     = diff === null ? '#999' : diff >= 0 ? '#1D9E75' : '#D85A30';
                      return (
                        <div key={i} className="period-row period-row-6">
                          <span>{fmtVecka(r[0])}</span>
                          <span>{intVal > 0 ? tkr(intVal) : '–'}</span>
                          <span style={{ color: '#999' }}>{fgVal > 0 ? tkr(fgVal) : '–'}</span>
                          <span style={{ color: dc, fontWeight: 500 }}>
                            {diff !== null ? (diff >= 0 ? '+' : '') + Math.round(diff * 100) + '%' : '–'}
                          </span>
                          <span>{r[3] ? tkr(r[3]) : '–'}</span>
                          <span style={{ color: belaggningColor(r[2] || 0), fontWeight: 500 }}>{r[2] > 0 ? pct(r[2]) : '–'}</span>
                        </div>
                      );
                    })}
                  </div>
                  <div className="note">Källa: Qlik — fg år = samma vecka föregående år</div>
                </div>
              </>
            );
          })()}

          {/* ── MÅNAD ── */}
          {periodSub === 'manad' && <>
          <div className="grid-4">
            <Metric
              label="Beläggning YTD"
              value={pct(belaggningYTD)}
              sub={`${ytd.bokadeRumsnatter || 0} / ${ytd.mojligaRumsnatter || 0} rumsnätter`}
            />
            <Metric
              label="Gästnätter YTD"
              value={fmt(gastnatterYTD)}
              sub={`Bokade gästnätter ${RA_LBL}`}
            />
            <Metric
              label="Snittpris logi/natt"
              value={snittpris > 0 ? snittpris.toLocaleString('sv-SE') + ' kr' : '–'}
              sub="Hotell Logi / gästnätter"
            />
            <Metric
              label="Hotell intäkt YTD"
              value={tkr(hotellTot)}
              sub={'TB1 ' + tkr(hotellTB1) + ' · TG ' + Math.round(hotellTG * 100) + '%'}
              trend={hotellTB1 > 0 ? 'up' : undefined}
            />
          </div>

          <div className="card">
            <div className="card-title">Intäkter över tid — {RA_LBL} vs fg år</div>
            <IntaktLineChart monthMap={monthMap} fgArKey={fgArKey} />
            <div className="note" style={{ marginTop: 8 }}>Källa: Qlik — Hotell (Logi + Rest + Förm. + Akt.)</div>
          </div>

          <div className="card">
            <div className="card-title">Månadsöversikt — intäkt, fg år, TB1 & beläggning</div>
            <div className="period-table">
              <div className="period-header period-row-7">
                <span>Månad</span><span>Intäkt</span><span>Fg år</span><span>+%</span><span>TB1</span><span>Belägg.</span><span>Pers%</span>
              </div>
              {visibleRows.map((r, i) => {
                const fg = monthMap[fgArKey(r[0])];
                const diff = fg && fg[1] > 0 ? (r[1] - fg[1]) / fg[1] : null;
                const dc = diff === null ? '#999' : diff >= 0 ? '#1D9E75' : '#D85A30';
                const lon = (r[42] || 0) + (r[43] || 0) + (r[44] || 0);
                const pers = r[1] > 0 ? Math.round(Math.abs(lon) / r[1] * 100) : 0;
                return (
                  <div key={i} className="period-row period-row-7">
                    <span>{fmtMonth(r[0])}</span>
                    <span>{r[1] > 0 ? tkr(r[1]) : '–'}</span>
                    <span style={{ color: '#999' }}>{fg && fg[1] > 0 ? tkr(fg[1]) : '–'}</span>
                    <span style={{ color: dc, fontWeight: 500 }}>
                      {diff !== null ? (diff >= 0 ? '+' : '') + Math.round(diff * 100) + '%' : '–'}
                    </span>
                    <span>{r[9] ? tkr(r[9]) : '–'}</span>
                    <span style={{ color: belaggningColor(r[8] || 0), fontWeight: 500 }}>{r[8] > 0 ? pct(r[8]) : '–'}</span>
                    <span>{r[1] > 0 && lon !== 0 ? `${pers}%` : '–'}</span>
                  </div>
                );
              })}
            </div>
            <div className="note">Källa: Qlik — Hotell (Logi + Rest + Förm. + Akt.), fg år = samma månad föregående år</div>
          </div>

          <div className="card">
            <div className="card-title">Beläggning % över tid — {RA_LBL} vs fg år</div>
            <BelaggningLineChart monthMap={monthMap} fgArKey={fgArKey} />
            <div className="note" style={{ marginTop: 8 }}>Källa: Qlik #BokadeRumsnätter / #MöjligaRumsnätter</div>
          </div>

          <div className="grid-2">
            <div className="card">
              <div className="card-title">Skydda — bärande perioder</div>
              <KpiRow label="V28–31 (sommar)"      value="" badge={{ type: 'green', text: 'Skydda' }} />
              <KpiRow label="V06–16 (vinter/påsk)" value="" badge={{ type: 'green', text: 'Skydda' }} />
              <KpiRow label="V52–01 (jul/nyår)"    value="" badge={{ type: 'green', text: 'Skydda' }} />
              <KpiRow label="Mars — toppmånad"     value="57%" badge={{ type: 'green', text: 'Rekord' }} />
            </div>
            <div className="card">
              <div className="card-title">Åtgärda — svaga perioder</div>
              <KpiRow label="Oktober"       value="2%"  badge={{ type: 'red',   text: 'Stäng'    }} />
              <KpiRow label="Maj"           value="0%"  badge={{ type: 'red',   text: 'Stäng'    }} />
              <KpiRow label="November"      value="8%"  badge={{ type: 'amber', text: 'Minimera' }} />
              <KpiRow label="Yield feb/apr" value=""    badge={{ type: 'amber', text: 'Stärk'    }} />
            </div>
          </div>
          </>}
        </>
      )}

      {/* ── FÖR PLANERING (hotellrestaurangen + bokningsläge) ── */}
      {sub === 'planering' && (
        <>
          <PlaneringTab enheter={['Hotellrestaurangen']} color="#1B2A4A" />
          <BokningslagePlan planDays={bookings?.planDays} planDaysLastYear={bookings?.planDaysLastYear} />
        </>
      )}

      {/* ── NÖRDERI ── */}
      {sub === 'norderi' && (() => {
        const bokadeRumYTD  = ytd.bokadeRumsnatter  || 0;
        const mojligaRumYTD = ytd.mojligaRumsnatter || 0;
        const logiYTD       = ytd.hotellLogi        || 0;
        const gNatterYTD    = ytd.gastnatter         || 0;
        const bokningarYTD  = ytd.bokningarHotell    || 0;

        const adr    = bokadeRumYTD  > 0 ? Math.round(logiYTD / bokadeRumYTD)  : null;
        const revpar = mojligaRumYTD > 0 ? Math.round(logiYTD / mojligaRumYTD) : null;
        const snGast = gNatterYTD    > 0 ? Math.round(logiYTD / gNatterYTD)    : null;
        const snVist = bokningarYTD  > 0 ? (gNatterYTD / bokningarYTD).toFixed(1) : null;

        // Bästa/sämsta månad per ADR (hotellLogi / bokadeRum per månad)
        const mRows = rows.filter(r => (r[10] || 0) > 0 && (r[34] || 0) > 0);
        const mAdr  = mRows.map(r => ({ arManad: r[0], adr: Math.round(r[10] / r[34]) }));
        const bestM  = mAdr.length ? mAdr.reduce((a, b) => b.adr > a.adr ? b : a) : null;
        const worstM = mAdr.length ? mAdr.reduce((a, b) => b.adr < a.adr ? b : a) : null;

        const kpiGridStyle = {
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
          gap: 20,
          padding: '20px 24px',
        };

        return (
          <>
            {/* YTD KPI:er */}
            <div className="card">
              <div className="card-title">Nörderi — Hotell YTD ({RA_LBL})</div>
              <div style={kpiGridStyle}>
                <NorderiKpi emoji="💰" label="ADR (snittpris/bokat rum)"
                  value={adr ? adr.toLocaleString('sv-SE') + ' kr' : '–'}
                  sub="Logi / bokade rumsnätter"
                />
                <NorderiKpi emoji="📊" label="RevPAR"
                  value={revpar ? revpar.toLocaleString('sv-SE') + ' kr' : '–'}
                  sub="Logi / möjliga rumsnätter"
                />
                <NorderiKpi emoji="🌙" label="Snittpris / gästnatt"
                  value={snGast ? snGast.toLocaleString('sv-SE') + ' kr' : '–'}
                  sub="Logi / bokade gästnätter"
                />
                <NorderiKpi emoji="🛏️" label="Beläggning YTD"
                  value={belaggningYTD > 0 ? Math.round(belaggningYTD * 100) + '%' : '–'}
                  sub={`${bokadeRumYTD.toLocaleString('sv-SE')} av ${mojligaRumYTD.toLocaleString('sv-SE')} rumsnätter`}
                  color={belaggningYTD >= 0.6 ? '#1D9E75' : belaggningYTD >= 0.3 ? '#E0A458' : '#D85A30'}
                />
                <NorderiKpi emoji="👥" label="Gästnätter YTD"
                  value={gNatterYTD.toLocaleString('sv-SE')}
                  sub="Bokade gästnätter"
                />
                <NorderiKpi emoji="📋" label="Antal bokningar YTD"
                  value={bokningarYTD > 0 ? bokningarYTD.toLocaleString('sv-SE') : '–'}
                  sub={snVist ? `Snitt ${snVist} nätter/bokning` : 'Hotell Logi'}
                />
              </div>
              {(bestM || worstM) && (
                <div style={{ display: 'flex', gap: 16, padding: '12px 24px', borderTop: '1px solid #E8E6E0' }}>
                  {bestM && (
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: '#1D9E75', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Bästa ADR-månad</div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>{fmtMonth(bestM.arManad)}</div>
                      <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{bestM.adr.toLocaleString('sv-SE')} kr/rum</div>
                    </div>
                  )}
                  {worstM && bestM?.arManad !== worstM?.arManad && (
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: '#D85A30', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Lägsta ADR-månad</div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>{fmtMonth(worstM.arManad)}</div>
                      <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{worstM.adr.toLocaleString('sv-SE')} kr/rum</div>
                    </div>
                  )}
                </div>
              )}
              <div className="note">Källa: Qlik — Hotell Logi · ADR = Logi / bokade rumsnätter · RevPAR = Logi / möjliga rumsnätter</div>
            </div>

            {/* Topp 5 rum */}
            <div className="card">
              <div className="card-title">Topp 5 mest bokade rum (alla tider)</div>
              {roomStatsLoading ? (
                <div style={{ textAlign: 'center', padding: 24, color: '#888', fontSize: 13, fontFamily: 'system-ui' }}>Hämtar rumdata från Qlik...</div>
              ) : roomStats === null ? (
                <div style={{ textAlign: 'center', padding: 24, color: '#888', fontSize: 13, fontFamily: 'system-ui' }}>Klicka på fliken för att ladda data</div>
              ) : roomStats.length === 0 ? (
                <div style={{ color: '#aaa', fontSize: 13, fontFamily: 'system-ui' }}>Ingen rumdata tillgänglig</div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'system-ui', fontSize: 13 }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #1B2A4A' }}>
                        {['#','Rum','Bokningar','Nätter','Gäster'].map(h => (
                          <th key={h} style={{ textAlign: h === 'Rum' ? 'left' : 'right', padding: '6px 10px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#555' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {roomStats.slice(0, 5).map((r, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #F0EFE9' }}>
                          <td style={{ padding: '8px 10px', color: '#aaa', fontSize: 11, textAlign: 'right' }}>{i + 1}</td>
                          <td style={{ padding: '8px 10px', fontWeight: 600, color: '#1B2A4A' }}>{r.enhet}</td>
                          <td style={{ padding: '8px 10px', textAlign: 'right', color: '#333' }}>{r.bokningar.toLocaleString('sv-SE')}</td>
                          <td style={{ padding: '8px 10px', textAlign: 'right', color: '#333' }}>{r.natter.toLocaleString('sv-SE')}</td>
                          <td style={{ padding: '8px 10px', textAlign: 'right', color: '#333' }}>{r.gaster.toLocaleString('sv-SE')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {roomStats.length > 5 && (
                    <div style={{ fontSize: 12, color: '#aaa', fontFamily: 'system-ui', marginTop: 8 }}>
                      Visar 5 av {roomStats.length} rum
                    </div>
                  )}
                </div>
              )}
              <div className="note">Källa: Qlik Bokningar — accommodation, status order/invoiced/payed · sorterat på antal nätter</div>
            </div>
          </>
        );
      })()}
    </div>
  );
}
